import { ApiResponse } from 'apisauce';

import { ExportJobCreateResult } from '@/Modules/MyFilesModule/shared/types/export-job.type';
import { exportData } from '@/Modules/ReportsModule/FinanceApplicationReport/shared/utils/helpers/utils';
import { PurchaseOrderReportStats } from '@/Modules/ReportsModule/PurchaseOrdersReport/shared/store/purchase-orders-reports.store';
import { mapQueueExportApiResponse } from '@/shared/utils/queue-export-job.util';
import { ErrorInfo } from '@/shared/interfaces/error-info.interface';
import { ResponseModel } from '@/shared/interfaces/response-model.interface';
import purchasesMapper from '@/shared/mappers/purchases/purchase-details.mapper';
import purchasesTableDataMapper from '@/shared/mappers/purchases/purchase-table.mapper';
import purchaseDocsMapper from '@/shared/mappers/purchases/purchases-docs.mapper';
import { PurchaseDocumentsDataModel, PurchasesModel } from '@/shared/models/purchases.model';
import { purchasesApi } from '@/shared/services/apis/purchases-apis/purchases-api.service';
import errorHelper from '@/shared/services/helpers/error-helper/error-helper.service';
import { DocsDataModel } from '@/shared/types/shared-types/documents.type';
import { PurchasesPayload } from '@/shared/ui-models/purchase.ui-model';
import {
  PurchaseDocsUIModel,
  PurchaseDocumentsDataUIModel,
  PurchaseOrderSearchQuery,
  PurchaseUiModel,
  PurchasesTableDataUiModel
} from '@/shared/ui-models/purchase.ui-model';

export async function createPurchase(
  transaction: PurchaseUiModel,
  containerName: string
): Promise<{ id: string } | ErrorInfo> {
  const mappedData = purchasesMapper.mapToModel(transaction);
  return purchasesApi
    .createPurchase(mappedData)
    .then((response: ApiResponse<{ id: string }, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data) {
          return data;
        } else {
          const error: ErrorInfo = errorHelper(
            0,
            '',
            containerName,
            (purchasesApi.createPurchase as any).name
          );
          return error;
        }
      } else {
        const error: ErrorInfo = errorHelper(
          Number(response.status),
          response.data && response.data.errorCode
            ? String(response.data.errorCode || '')
            : response.problem,
          containerName,
          (purchasesApi.createPurchase as any).name
        );
        return error;
      }
    });
}

export async function searchPurchases(
  searchPurchases: PurchasesPayload,
  containerName: string
): Promise<ResponseModel<PurchasesTableDataUiModel[]> | ErrorInfo> {
  const payload = {
    skip: searchPurchases.skip || 0,
    limit: searchPurchases.limit || 10,
    query: {
      ...searchPurchases.query,
      autoProfitMargin: searchPurchases.query?.autoProfitMargin
        ? {
            from: searchPurchases.query.autoProfitMargin.from || undefined,
            to: searchPurchases.query.autoProfitMargin.to || undefined
          }
        : undefined,
      startDate: searchPurchases.query?.startDate
        ? new Date(searchPurchases.query.startDate)
        : undefined,
      endDate: searchPurchases.query?.endDate ? new Date(searchPurchases.query.endDate) : undefined
    },
    sort: searchPurchases.sort
  };

  return purchasesApi
    .searchPurchases(payload)
    .then((response: ApiResponse<ResponseModel<PurchasesModel[]>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data) {
          const mappedData = {
            ...data,
            data: purchasesTableDataMapper.mapToUi(data.data),
            documents_counts: data.documents_counts ?? 0
          };
          return mappedData;
        } else {
          const error: ErrorInfo = errorHelper(
            0,
            '',
            containerName,
            (purchasesApi.searchPurchases as any).name
          );
          return error;
        }
      } else {
        const error: ErrorInfo = errorHelper(
          Number(response.status),
          response.data && response.data.errorCode
            ? String(response.data.errorCode || '')
            : response.problem,
          containerName,
          (purchasesApi.searchPurchases as any).name
        );
        return error;
      }
    });
}

export async function getPurchaseById(
  id: string,
  containerName: string
): Promise<PurchaseUiModel | ErrorInfo> {
  return purchasesApi
    .getPurchaseById(id)
    .then((response: ApiResponse<ResponseModel<PurchasesModel>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data) {
          const mappedData = purchasesMapper.mapToUi(data as unknown as PurchasesModel);
          return mappedData;
        } else {
          const error: ErrorInfo = errorHelper(
            0,
            '',
            containerName,
            (purchasesApi.getPurchaseById as any).name
          );
          return error;
        }
      } else {
        const error: ErrorInfo = errorHelper(
          Number(response.status),
          response.data && response.data.errorCode
            ? String(response.data.errorCode || '')
            : response.problem,
          containerName,
          (purchasesApi.getPurchaseById as any).name
        );
        return error;
      }
    });
}

export async function updatePurchase(
  transaction: PurchaseUiModel,
  containerName: string
): Promise<ResponseModel<PurchasesModel> | ErrorInfo> {
  const mappedData = purchasesMapper.mapToModel(transaction);

  return purchasesApi
    .updatePurchase(mappedData)
    .then((response: ApiResponse<ResponseModel<PurchasesModel>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data) {
          return data;
        } else {
          const error: ErrorInfo = errorHelper(
            0,
            '',
            containerName,
            (purchasesApi.updatePurchase as any).name
          );
          return error;
        }
      } else {
        const error: ErrorInfo = errorHelper(
          Number(response.status),
          response.data && response.data.errorCode
            ? String(response.data.errorCode || '')
            : response.problem,
          containerName,
          (purchasesApi.updatePurchase as any).name
        );
        return error;
      }
    });
}

export async function createPurchaseDocuments(
  purchaseDocuments: PurchaseDocumentsDataUIModel,
  containerName: string
): Promise<{ message: string } | ErrorInfo | undefined> {
  const data = new FormData();
  const mappedData = purchaseDocsMapper.mapToModel(purchaseDocuments);

  if (!mappedData.purchaseOrderId) return { message: '' };

  data.append('id', mappedData.purchaseOrderId);
  let hasFiles = false;
  for (const property in mappedData) {
    if (property !== 'purchaseOrderId') {
      (mappedData[property as keyof PurchaseDocsUIModel] as DocsDataModel[]).forEach(
        (file: DocsDataModel) => {
          if ((file as unknown as Blob).size) {
            hasFiles = true;
            data.append(property, file as unknown as Blob);
          }
        }
      );
    }
  }
  if (!hasFiles) return { message: '' };

  return purchasesApi
    .createPurchaseDocuments(data)
    .then((response: ApiResponse<{ message: string }, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data) {
          return data;
        }
      } else {
        const error: ErrorInfo = errorHelper(
          Number(response.status),
          response.data && response.data.errorCode
            ? String(response.data.errorCode || '')
            : response.problem,
          containerName,
          (purchasesApi.createPurchaseDocuments as any).name
        );
        return error;
      }
    });
}

export async function getPurchaseDocumentsById(
  id: string,
  containerName: string
): Promise<PurchaseDocumentsDataUIModel | ErrorInfo> {
  return purchasesApi
    .getPurchaseDocumentsById(id)
    .then((response: ApiResponse<ResponseModel<PurchaseDocumentsDataModel>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data) {
          const mappedData = purchaseDocsMapper.mapToUI(
            data as unknown as PurchaseDocumentsDataModel
          );
          return mappedData;
        } else {
          const error: ErrorInfo = errorHelper(
            0,
            '',
            containerName,
            (purchasesApi.getPurchaseDocumentsById as any).name
          );
          return error;
        }
      } else {
        const error: ErrorInfo = errorHelper(
          Number(response.status),
          response.data && response.data.errorCode
            ? String(response.data.errorCode || '')
            : response.problem,
          containerName,
          (purchasesApi.getPurchaseDocumentsById as any).name
        );
        return error;
      }
    });
}

export async function deletePurchaseDocuments(
  key: string,
  containerName: string
): Promise<ResponseModel<PurchaseDocumentsDataModel> | ErrorInfo | undefined> {
  return purchasesApi
    .deletePurchaseDocuments(key)
    .then((response: ApiResponse<ResponseModel<PurchaseDocumentsDataModel>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        return data;
      } else {
        const error: ErrorInfo = errorHelper(
          Number(response.status),
          response.data && response.data.errorCode
            ? String(response.data.errorCode || '')
            : response.problem,
          containerName,
          (purchasesApi.deletePurchaseDocuments as any).name
        );
        return error;
      }
    });
}

export async function getPurchaseSignedUrl(
  key: string,
  containerName: string
): Promise<{ url: string } | ErrorInfo | undefined> {
  return purchasesApi
    .getPurchaseSignedUrl(key)
    .then((response: ApiResponse<{ url: string }, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        console.log(data);
        return data;
      } else {
        const error: ErrorInfo = errorHelper(
          Number(response.status),
          response.data && response.data.errorCode
            ? String(response.data.errorCode || '')
            : response.problem,
          containerName,
          (purchasesApi.getPurchaseSignedUrl as any).name
        );
        return error;
      }
    });
}

export async function generateTrustReceipt(
  purchaseOrderId: string,
  containerName: string
): Promise<ErrorInfo | undefined> {
  return exportData<{ query: { purchaseOrderId: string } }>(
    (payload) => purchasesApi.generateTrustReceipt(payload.query.purchaseOrderId),
    { query: { purchaseOrderId }, sort: {} },
    containerName,
    'trust-receipt',
    'docx'
  );
}

export async function generateReceiptAcknowledgment(
  purchaseOrderId: string,
  containerName: string
): Promise<ErrorInfo | undefined> {
  return exportData<{ query: { purchaseOrderId: string } }>(
    (payload) => purchasesApi.generateReceiptAcknowledgment(payload.query.purchaseOrderId),
    { query: { purchaseOrderId }, sort: {} },
    containerName,
    'receipt-acknowledgment',
    'docx'
  );
}

export async function generateReceiptAcknowledgmentAndLicensingCommitment(
  purchaseOrderId: string,
  containerName: string
): Promise<ErrorInfo | undefined> {
  return exportData<{ query: { purchaseOrderId: string } }>(
    (payload) =>
      purchasesApi.generateReceiptAcknowledgmentAndLicensingCommitment(
        payload.query.purchaseOrderId
      ),
    { query: { purchaseOrderId }, sort: {} },
    containerName,
    'receipt-acknowledgment-and-licensing-commitment',
    'docx'
  );
}

export async function generatePurchaseOrder(
  purchaseOrderId: string,
  containerName: string
): Promise<ErrorInfo | undefined> {
  return exportData<{ query: { purchaseOrderId: string } }>(
    (payload) => purchasesApi.generatePurchaseOrder(payload.query.purchaseOrderId),
    { query: { purchaseOrderId }, sort: {} },
    containerName,
    'purchase-order',
    'xlsx'
  );
}

export async function getPurchaseOrdersReportStats(
  payload: PurchaseOrderSearchQuery,
  containerName: string
): Promise<ResponseModel<PurchaseOrderReportStats> | ErrorInfo> {
  return purchasesApi
    .getPurchaseOrdersReportStats(payload)
    .then((response: ApiResponse<ResponseModel<PurchaseOrderReportStats>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data) {
          return data;
        } else {
          const error: ErrorInfo = errorHelper(
            0,
            '',
            containerName,
            (purchasesApi.getPurchaseOrdersReportStats as any).name
          );
          return error;
        }
      } else {
        const error: ErrorInfo = errorHelper(
          Number(response.status),
          response.data && response.data.errorCode
            ? String(response.data.errorCode || '')
            : response.problem,
          containerName,
          (purchasesApi.getPurchaseOrdersReportStats as any).name
        );
        return error;
      }
    });
}

export async function exportPurchaseOrderProfitReport(
  payload: Pick<PurchasesPayload, 'query' | 'sort'>,
  containerName: string
): Promise<ExportJobCreateResult | ErrorInfo | undefined> {
  const response = await purchasesApi.exportPurchaseOrderProfitReport(
    payload as Parameters<typeof purchasesApi.exportPurchaseOrderProfitReport>[0]
  );
  return mapQueueExportApiResponse(
    response,
    containerName,
    purchasesApi.exportPurchaseOrderProfitReport.name
  );
}
